
import { RevButtonRounded } from '@ds/components/ButtonRounded'
import { RevButtonTiny } from '@ds/components/ButtonTiny'
import { IconHeart } from '@ds/icons/IconHeart'
import { IconHeartFilled } from '@ds/icons/IconHeartFilled'
import { mapGetters } from 'vuex'

import {
  createFavoriteForUser,
  deleteUserFavorite,
  getFavoritesFromProduct,
} from '@http/endpoints'
import logger from '@logger'
import { ROUTES } from '@router'
import { PAGE_TYPE_BY_ROUTE_NAME } from '@tracking/constants'
import { trackAddToMyFavorites, trackClick } from '@tracking/events'

import { LOG_ERROR_MESSAGE } from '../../MyFavorites.constants'
import { TOAST_TYPE } from '../notification/Notification.constants'
import Notification from '../notification/Notification.vue'

import {
  MY_FAVORITES_ACTIONS,
  MY_FAVORITES_STORAGE_PREFIX,
} from './MyFavoritesToggle.constants'
import translations from './MyFavoritesToggle.translations'

export default {
  components: {
    Notification,
    RevButtonTiny,
    RevButtonRounded,
  },

  props: {
    isRounded: {
      type: Boolean,
      default: false,
    },

    isSmallScreen: {
      type: Boolean,
      default: false,
    },

    trackingInfo: {
      type: Object,
      default: () => ({}),
    },

    zone: {
      type: String,
      default: 'detail',
    },

    favoriteId: {
      type: String,
      default: '',
    },

    productUuid: {
      type: String,
      required: true,
    },

    offerType: {
      type: Number,
      default: 0,
    },

    gradeId: {
      type: Number,
      required: true,
    },

    price: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      itemFavoriteId: this.favoriteId,

      isFavorite: Boolean(this.favoriteId),

      toast: {
        isOpen: false,
        type: TOAST_TYPE.SAVED,
      },

      isUndoAvailable: false,
    }
  },

  async fetch() {
    this.initialAction()
  },

  computed: {
    ...mapGetters({
      isAuthenticated: 'auth/status',
      isNewAuthEnabled: 'auth/isNewAuthEnabled',
    }),

    translations: () => translations,

    buttonLabel() {
      return this.isFavorite
        ? this.$t(translations.favoritesButtonRemoveLabel)
        : this.$t(translations.favoritesButtonAddLabel)
    },

    heartComponentVariant() {
      return this.isFavorite ? IconHeartFilled : IconHeart
    },

    hasPendingAction() {
      return Boolean(sessionStorage.getItem(this.uniqueStorageKey))
    },

    uniqueStorageKey() {
      const key = `${MY_FAVORITES_STORAGE_PREFIX}_${this.productUuid}_${this.gradeId}_${this.offerType}`
      const smallScreenKey = `${key}_small`

      return this.isSmallScreen ? smallScreenKey : key
    },
  },

  watch: {
    isAuthenticated() {
      this.initialAction()
    },

    productUuid() {
      this.optionUpdated()
    },

    // includes both offerType and gradeId
    '$route.hash': function optionUpdated() {
      this.optionUpdated()
    },
  },

  beforeDestroy() {
    if (this.toast.isOpen && this.toast.type === TOAST_TYPE.UNDO) {
      this.handleUndoToastClosed()
    }
  },

  methods: {
    optionUpdated() {
      this.resetData()
      this.initialAction()
    },

    resetData() {
      this.itemFavoriteId = this.favoriteId
      this.isFavorite = Boolean(this.favoriteId)
      this.isUndoAvailable = false
      this.toast = {
        isOpen: false,
        type: TOAST_TYPE.SAVED,
      }
    },

    async initialAction() {
      if (!this.isAuthenticated) {
        return
      }

      await this.getFavoriteFromProduct()
      this.executePendingAction()
    },

    hideToast() {
      this.toast.isOpen = false
      this.isUndoAvailable = false
    },

    displayToast(toastType) {
      this.toast.type = toastType
      this.toast.isOpen = true
    },

    handleUndoToastClosed() {
      this.hideToast()
      this.executePendingAction()
    },

    handleUndoAction() {
      this.isFavorite = true
      this.hideToast()
      this.clearPendingAction()
    },

    trackAddClick() {
      trackAddToMyFavorites(this.trackingInfo)
    },

    trackRemoveClick() {
      const routeName = this.$route.name

      trackClick({
        name: 'remove_from_my_favorites',
        zone: this.zone,
        pageType: PAGE_TYPE_BY_ROUTE_NAME[routeName],
      })
    },

    isPendingFavoriteMatchingProduct({ productUuid, gradeId }) {
      const areProductUuidsMatching = productUuid === this.productUuid
      const areGradeIdsMatching = gradeId === this.gradeId

      return areProductUuidsMatching && areGradeIdsMatching
    },

    executePendingAction() {
      if (!process.client) {
        return
      }

      const pendingAction = sessionStorage.getItem(this.uniqueStorageKey)
      this.clearPendingAction()

      if (!pendingAction || !this.isAuthenticated) {
        return
      }

      try {
        const { action } = JSON.parse(pendingAction)

        if (action === MY_FAVORITES_ACTIONS.ADD) {
          this.addToFavorites()
        } else if (action === MY_FAVORITES_ACTIONS.REMOVE) {
          this.removeFromFavorites()
        }
      } catch {
        // fails silently (json structure exception)
      }
    },

    clearPendingAction() {
      sessionStorage.removeItem(this.uniqueStorageKey)
    },

    storePendingAction(action) {
      const pendingAction = JSON.stringify({ action })

      sessionStorage.setItem(this.uniqueStorageKey, pendingAction)
    },

    requestAuthentication() {
      const next = this.$route.fullPath

      // [LIF-1229] TODO: remove legacy auth route when the new one is on production
      let authLink = {
        name: ROUTES.AUTH.REGISTER,
        query: {
          ...this.$route.query,
          next,
        },
      }

      if (this.isNewAuthEnabled) {
        authLink = {
          name: ROUTES.AUTH_OAUTH2.LOGIN,
          query: {
            ...this.$route.query,
            next,

            // needed for the info toast on Auth page
            info: 'my-favorites',
          },
        }
      }

      this.$navigation.push(authLink)
    },

    async getFavoriteFromProduct() {
      if (this.isFavorite || !this.isAuthenticated) {
        return
      }

      try {
        const { productUuid, gradeId, offerType } = this

        const {
          payload: [favorite],
        } = await this.$store.dispatch('http/request', {
          request: getFavoritesFromProduct,
          pathParams: { productUuid },
          queryParams: { gradeId, offerType },
        })

        if (!favorite) {
          return
        }

        this.itemFavoriteId = favorite.favoriteId
        this.isFavorite = true
      } catch (error) {
        if (error.status_code === 401) {
          return
        }

        logger.error(LOG_ERROR_MESSAGE.CHECK, error)
      }
    },

    async addToFavorites() {
      if (this.isFavorite) {
        this.displayToast(TOAST_TYPE.ALREADY_SAVED)

        return
      }

      try {
        const body = {
          productUuid: this.productUuid,
          gradeId: this.gradeId,
          price: this.price,
          offerType: this.offerType,
        }

        const {
          payload: { favoriteId },
        } = await this.$store.dispatch('http/request', {
          request: createFavoriteForUser,
          body,
        })

        this.displayToast(TOAST_TYPE.SAVED)

        this.itemFavoriteId = favoriteId
        this.isFavorite = true
      } catch (error) {
        logger.error(LOG_ERROR_MESSAGE.ADD, error)
      }
    },

    async removeFromFavorites() {
      try {
        const favoriteId = this.itemFavoriteId

        await this.$store.dispatch('http/request', {
          request: deleteUserFavorite,
          pathParams: { favoriteId },
        })

        this.itemFavoriteId = ''
        this.isFavorite = false

        this.$emit('removed')
      } catch (error) {
        logger.error(LOG_ERROR_MESSAGE.REMOVE, error)
      }
    },

    handleClick() {
      this.hideToast()

      const doTrackClick = this.isFavorite
        ? this.trackRemoveClick
        : this.trackAddClick
      doTrackClick()

      if (!this.isAuthenticated) {
        this.storePendingAction(MY_FAVORITES_ACTIONS.ADD)

        this.requestAuthentication()

        return
      }

      if (this.isFavorite) {
        this.storePendingAction(MY_FAVORITES_ACTIONS.REMOVE)

        this.isUndoAvailable = true
        this.displayToast(TOAST_TYPE.UNDO)

        // optimistic UI:
        // displays an empty heart right away
        // even though the favorite has not been removed from the database yet
        this.isFavorite = false

        return
      }

      this.addToFavorites()
    },
  },
}
